/* eslint-disable import/first */
// ─── Sentry init — DOIT être en tête de fichier, avant tout autre import ───────
// SPEC-2026-06-08-sentry-sdk-integration §4 — amendé 2026-06-12 :
//   #6 ignoreErrors + denyUrls (bruit wallet/browser-ext supprimé)
//   #7 BrowserTracing + tracesSampleRate:0.05 (5% sampling perf)
// NB: `import/first` désactivé — Sentry.init() doit s'exécuter AVANT les autres imports.
import * as Sentry from '@sentry/react';
import { scrubbedEvent } from './utils/functions/sentryUtils';

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_SENTRY_ENV || 'development',
  release: process.env.REACT_APP_SENTRY_RELEASE || 'unknown',
  tracesSampleRate: 0.05,
  integrations: [
    Sentry.browserTracingIntegration(),
    // SPEC-2026-07-01 — capture 100% : tout console.error front (catch loggués,
    // ErrorBoundary.jsx scoped) devient un event Sentry. Guard défensif si l'API
    // n'existe pas dans la version du SDK installée.
    ...(typeof (Sentry as any).captureConsoleIntegration === 'function'
      ? [(Sentry as any).captureConsoleIntegration({ levels: ['error'] })]
      : []),
  ],
  enabled: !!process.env.REACT_APP_SENTRY_DSN,
  beforeSend: scrubbedEvent,
  ignoreErrors: [
    /User rejected/i,
    /user denied/i,
    /User cancelled/i,
    /MetaMask/,
    /WalletConnect/,
    /Failed to fetch/,
    /Load failed/,
    /Network request failed/,
    /NetworkError/,
    /ChunkLoadError/,
    /Loading chunk/,
    /ResizeObserver loop/,
    /Non-Error promise rejection/,
    /Object captured as promise rejection/,
  ],
  denyUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
  ],
});
// ─────────────────────────────────────────────────────────────────────────────────

import React from 'react';
import ReactDOM from 'react-dom';
// import './index.css';
import "./assets/styles/animated.css";
import '../node_modules/font-awesome/css/font-awesome.min.css';
import '../node_modules/elegant-icons/style.css';
import '../node_modules/et-line/style.css';
import '../node_modules/bootstrap/dist/css/bootstrap.min.css';
import '../node_modules/bootstrap/dist/js/bootstrap.js';
import './assets/styles/style.scss';
import './assets/styles/style_grey.scss';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux'
import store from './store';
import { readStoredUser } from './utils/auth/userStorage';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import AppKitProvider from './AppKitProvider';
import { WalletProvider } from './components/EarniWalletModal';
import SentryGlobalErrorFallback from './components/ErrorBoundary.global';

// Initialize languages
import './locales/i18n';
import { SERVER_URL } from './core/config';

/*
 * Ordre de montage §8.2 (EarniConnect SPEC) :
 *   AppKitProvider (wagmi/WagmiProvider + QueryClientProvider)
 *   └─ WalletProvider (context EarniWalletModal — fournit openModal/closeModal)
 *      └─ Redux Provider (store applicatif — pas d'accès Redux dans WalletModal)
 *         └─ App
 *
 * apiBaseUrl et chainId sont fournis ici ; ils transitent via context
 * jusqu'à EarniWalletModal sans passer par Redux.
 *
 * Sentry.ErrorBoundary wraps the whole tree (outermost meaningful boundary).
 * Le fallback SentryGlobalErrorFallback est affiché en cas de crash global.
 * Les ErrorBoundary scoped (ex: ErrorBoundary.jsx tile-map) restent inchangés.
 */
/* Dark mode — pré-apply anti-FOUC (avant le 1er rendu React) : un utilisateur connecté dont
 * preferredTheme==='dark' voit tout de suite le fond sombre, sans flash clair. L'applicateur
 * unique (effet du Header) reprend ensuite la main et gère login/logout. Le dark exige d'être
 * connecté → on ne lit que _user (avec accessToken). */
try {
  const _u = readStoredUser();
  if (_u && _u.accessToken && _u.preferredTheme === 'dark') {
    document.documentElement.setAttribute('data-theme', 'dark');
  }
} catch (e) { /* localStorage indisponible — ignore */ }

ReactDOM.render(
  <Sentry.ErrorBoundary fallback={<SentryGlobalErrorFallback />}>
    <AppKitProvider>
      <WalletProvider
        apiBaseUrl={SERVER_URL || ''}
        chainId={Number(process.env.REACT_APP_AVAX_CHAIN_ID)}
      >
        <Provider store={store}>
          <HelmetProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </HelmetProvider>
        </Provider>
      </WalletProvider>
    </AppKitProvider>
  </Sentry.ErrorBoundary>,
  document.getElementById('root')
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
